import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";

export function Section({
  children,
  className,
  tone = "canvas",
  id,
  labelledBy,
}: {
  children: ReactNode;
  className?: string;
  tone?: "canvas" | "paper" | "ink" | "forest";
  id?: string;
  labelledBy?: string;
}) {
  const tones = {
    canvas: "bg-background text-foreground",
    paper: "bg-paper text-foreground",
    ink: "bg-ink text-canvas",
    forest: "bg-forest text-canvas",
  } as const;
  return (
    <section
      id={id}
      aria-labelledby={labelledBy}
      className={cn("relative isolate py-[clamp(3.5rem,7vw,7rem)]", tones[tone], className)}
    >
      {children}
    </section>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  lede,
  id,
  align = "left",
  tone = "dark",
  action,
  level = 2,
}: {
  eyebrow?: string;
  title: ReactNode;
  lede?: ReactNode;
  id?: string;
  align?: "left" | "center";
  tone?: "dark" | "light";
  action?: ReactNode;
  level?: 2 | 3;
}) {
  const H = (level === 2 ? "h2" : "h3") as "h2";
  return (
    <div
      className={cn(
        "flex flex-col gap-6 md:flex-row md:items-end md:justify-between",
        align === "center" && "md:flex-col md:items-center md:text-center",
      )}
    >
      <div className={cn("max-w-2xl", align === "center" && "mx-auto text-center")}>
        {eyebrow && (
          <p className={cn("eyebrow", tone === "light" && "text-brass-glow")}>{eyebrow}</p>
        )}
        <H id={id} className={cn("display-lg mt-3", tone === "light" ? "text-canvas" : "text-ink")}>
          {title}
        </H>
        {lede && (
          <p className={cn("lede mt-5", tone === "light" && "text-canvas/75", align === "center" && "mx-auto")}>
            {lede}
          </p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function Breadcrumbs({ trail }: { trail: { label: string; to?: string }[] }) {
  return (
    <nav aria-label="Breadcrumb" className="shell pt-8">
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        {trail.map((item, i) => (
          <li key={item.label} className="flex items-center gap-2">
            {item.to ? (
              <Link to={item.to} className="hover:text-ink hover:underline underline-offset-4">
                {item.label}
              </Link>
            ) : (
              <span aria-current="page" className="text-ink">
                {item.label}
              </span>
            )}
            {i < trail.length - 1 && <span aria-hidden="true">/</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function PageHeader({
  eyebrow,
  title,
  lede,
  children,
}: {
  eyebrow: string;
  title: string;
  lede: string;
  children?: ReactNode;
}) {
  return (
    <header className="shell pt-10 pb-[clamp(2.5rem,5vw,4.5rem)]">
      <p className="eyebrow">{eyebrow}</p>
      <h1 className="display-xl mt-4 max-w-4xl">{title}</h1>
      <p className="lede mt-6">{lede}</p>
      {children && <div className="mt-8 flex flex-wrap gap-3">{children}</div>}
    </header>
  );
}
